import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    CardItem,
    StatusCard,
    inject,
    PopoutHandle,
    bind,
    ModalHandle,
    ItemCarousel,
} from 'Mri.Web.Common';
import * as React from 'react';
import { AppStore } from '../../Core/AppStore';
import { UnitsStore } from '../../Units/UnitsStore';
import { ActiveDisplay } from '../ActiveDisplay';
import { DisplayDetailsPopout } from '../DisplayDetailsPopout';
import { DisplayScreenSnapshot } from '../DisplayScreenSnapshot';
import { DisplaysStore } from '../DisplaysStore';

export interface IDisplayUnitCardProps {
    readonly display: ActiveDisplay;
    readonly unit: Mri.Units.Unit;
    readonly loading?: boolean;
}

@autoinject
@observer
export class DisplayUnitCard extends React.Component<IDisplayUnitCardProps> {
    public render() {
        const { unit } = this.props;

        return (
            <StatusCard title={`Unit ${unit.serialNumber}`} loading={this.props.loading}>
                <CardItem label="Description" value={unit.description} />
                <CardItem label="Part Number" value={unit.partNumber} />

                <div className="status-list-title border-0">Displays</div>
                <div className="container-fluid px-2">
                    {this.unitDisplays.map((d, idx) => (
                        <UnitDisplayCardItem
                            key={d.serialNumber}
                            display={d}
                            viewingDisplay={d.serialNumber == this.props.display.serialNumber}
                        />
                    ))}
                </div>
            </StatusCard>
        );
    }

    @computed
    private get unitDisplays() {
        const displays: ActiveDisplay[] = [];
        const unit = this.unitsStore.units.get(this.props.display.unitSerialNumber);
        const unitDisplays = unit?.displays;
        if (unitDisplays) {
            for (const display of unitDisplays) {
                const d = this.displaysStore.displays.get(display);
                if (d) displays.push(d);
            }
        }

        return displays;
    }

    @inject
    private unitsStore!: UnitsStore;
    @inject
    private displaysStore!: DisplaysStore;
}

export interface IUnitDisplayCardItemProps {
    readonly display: ActiveDisplay;
    readonly viewingDisplay: boolean;
}

@autoinject
@observer
export class UnitDisplayCardItem extends React.Component<IUnitDisplayCardItemProps> {
    public async componentDidMount() {
        try {
            await this.displaysStore.loadLatestScreenSnapshotAsync(this.props.display.serialNumber);
        } catch (e) {
            console.log(e);
        }
    }
    public render() {
        const { display } = this.props;
        let statusClassName: string;

        switch (display.state) {
            default:
            case 'good':
                statusClassName = '';
                break;
            case 'exception':
                statusClassName = 'text-info';
                break;
            case 'warning':
                statusClassName = 'text-warning';
                break;
            case 'critical':
                statusClassName = 'text-danger';
                break;
        }

        return (
            <div className="row border-top py-3">
                <div className="col-md-6">
                    <div className="flex-items-center h-100">
                        <div className="d-inline-block px-1">
                            <a onClick={this.openPopout} className="btn btn-link p-0">
                                {display.serialNumber}
                            </a>
                            {this.props.viewingDisplay && <span className="badge bg-success mx-2">Viewing</span>}
                            <div>
                                Status: &nbsp;
                                <span className={statusClassName}>{display.statusMessageBrief}</span>
                            </div>

                            <div>
                                Events: &nbsp;
                                {display.newDisplayEventCount}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-6">
                    {this.screenSnapshots && (
                        <ItemCarousel
                            className="rounded"
                            items={this.screenSnapshots}
                            renderItem={i => <DisplayScreenSnapshot screenSnapshot={i} />}
                        />
                    )}
                </div>

                <DisplayDetailsPopout statusPoll={this.statusPoll} display={display} popoutHandle={this.popoutHandle} />
            </div>
        );
    }

    @bind
    @action
    private openDisplayEventsModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();

        this.displayEventsModalHandle.open();
    }

    @bind
    @action
    private openPopout() {
        this.popoutHandle.open();
    }

    @computed
    private get activeDisplayStatus() {
        if (this.props.display) return this.displaysStore.statuses.get(this.props.display.serialNumber);
    }

    @computed
    private get statusPoll() {
        if (this.activeDisplayStatus) return this.activeDisplayStatus.lastKnownPoll.displayStatusPoll;
    }

    @computed
    private get screenSnapshots() {
        const { display } = this.props;
        return this.displaysStore.screenSnapshots.get(display.serialNumber);
    }

    private popoutHandle = new PopoutHandle();
    private displayEventsModalHandle = new ModalHandle();
    @inject
    private unitsStore!: UnitsStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private appStore!: AppStore;
}
